import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

export interface Option {
  value: any;
  text: string;
}
@Component({
  selector: 'app-input-select',
  templateUrl: './input-select.component.html',
  styleUrls: ['./input-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputSelectComponent,
      multi: true,
    },
  ],
})
export class InputSelectComponent implements ControlValueAccessor {
  @Input() label!: string;
  @Input() name?: string;
  @Input() options!: Option[];
  @Input() showSuccessIcon: boolean = false;
  @Input() multiple: boolean = false;
  @Input() placeholder: string = '';
  /**
   * Opt-in (CPK-4555): search field inside the panel, select all / clear shortcuts
   * (multiple only) and a wider panel where long labels wrap instead of clipping.
   */
  @Input() searchable: boolean = false;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  searchText = '';

  field = new FormControl<any>('');
  constructor(private translate: TranslateService) {}

  /**
   * Option text is sometimes an i18n key, but usually a raw DB label (categories, tags,
   * statuses…). The bare translate pipe breaks when a raw label collides with an i18n
   * NAMESPACE — instant('KYC') returns the whole nested object and the option renders
   * "[object Object]". Anything non-string falls back to the label itself.
   */
  optionText(text: string): string {
    if (!text) {
      return text;
    }
    const translated = this.translate.instant(text);
    return typeof translated === 'string' ? translated : text;
  }

  /**
   * Search hides options via CSS instead of removing them from the DOM, so selected
   * options that stop matching keep their mat-option (selection + trigger text intact).
   * Matches against the displayed (translated) label, not the raw value.
   */
  isOptionVisible(option: Option): boolean {
    const search = this.searchText.trim().toLowerCase();
    if (!this.searchable || !search) {
      return true;
    }
    return this.optionText(option.text).toLowerCase().includes(search);
  }

  get hasVisibleOptions(): boolean {
    return (this.options ?? []).some((option) => this.isOptionVisible(option));
  }

  onSearchInput(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  /** mat-select grabs keydown for typeahead (space selects, letters jump) — keep typing in the box. */
  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  }

  onOpenedChange(opened: boolean): void {
    this.searchText = '';
    if (opened && this.searchable) {
      // Panel content is created on open; wait a tick for the ViewChild to resolve.
      setTimeout(() => this.searchInput?.nativeElement.focus());
    }
  }

  selectAllVisible(): void {
    if (!this.multiple) {
      return;
    }
    const current: any[] = Array.isArray(this.field.value) ? this.field.value : [];
    const merged = [...current];
    for (const option of this.options ?? []) {
      if (this.isOptionVisible(option) && !merged.includes(option.value)) {
        merged.push(option.value);
      }
    }
    this.value = merged;
  }

  clearSelection(): void {
    this.value = this.multiple ? [] : '';
  }

  onChange: any = () => {};
  onTouch: any = () => {};

  // sets the value used by the ngModel of the element
  set value(val: any) {
    this.field.setValue(val);
    this.onChange(val);
    this.onTouch(val);
  }

  // This will will write the value to the view if the the value changes occur on the model programmatically
  writeValue(value: any) {
    this.field.setValue(value);
  }

  // When the value in the UI is changed, this method will invoke a callback function
  registerOnChange(fn: (value: any) => void) {
    this.field.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: Function) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  disabled = false;
}
